import { useEffect } from 'react';
import { EVENTS } from 'common/constants';

/**
 * Hook that listens for a specified event on the window and document objects.
 * Automatically removes the event listener when the component is unmounted.
 * @param type - The name of the event to listen for.
 * @param callback - The function to call when the event is triggered.
 * @param immediate - Whether to call the callback function immediately.
 */
const useEventListener = (
  type: EVENTS | keyof WindowEventMap,
  callback: (e?: Event) => void,
  immediate?: boolean
): void => {
  const eventHandler = (e?: Event) => {
    callback(e);
  };

  useEffect(() => {
    const isSupported = Boolean(window?.addEventListener) || Boolean(document?.addEventListener);

    if (!isSupported || !type) {
      return;
    }

    if (immediate) {
      eventHandler();
    }

    window.addEventListener(type, eventHandler);
    document.addEventListener(type, eventHandler);

    return () => {
      window.removeEventListener(type, eventHandler);
      document.removeEventListener(type, eventHandler);
    };
  }, [type, callback]);
};

export default useEventListener;
